/* eslint-disable @typescript-eslint/naming-convention */
import {Component, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {AccessType, CHAT_MESSAGES_LIMIT} from '@app/modules/shared/data/constants';
import {Message, MessageType} from '@app/models/message';
import {CommandService} from '@app/services/command.service';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {ChatService} from '@app/modules/chat/services/chat.service';
import {RemoteUserAdditionalInfoStateModel, RemoteUsersInfoState} from '@app/state/remote-users.state';
import {MessageRecord} from '@app/services/data.service';
import {UtilService} from '@app/modules/shared/services/util.service';

@Component({
  selector: 'app-chat-box',
  templateUrl: './chat-box.component.html'
})
export class ChatBoxComponent implements OnInit {

  @Input()
  public height: number;

  @ViewChild('studentInputMessage')
  public studentChatInput;

  public AccessType = AccessType;
  public MessageType = MessageType;
  public UtilService = UtilService;
  public inputValue: string = '';
  public lastInputValue: string = this.inputValue;
  public classroom: ClassroomStateModel;
  public localUser: LocalUser;
  public remoteUsers: { [key: number]: RemoteUserAdditionalInfoStateModel };
  public isInResizeState = false;
  public mouse: { x: number; y: number };
  public showPopUp: boolean = false;

  constructor(
    public utilService: UtilService,
    private commandService: CommandService,
    private chatService: ChatService,
    private store: Store
  ) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {
    this.chatService.init();
    if (this.localUser.accessType === AccessType.STUDENT) {
      this.store.dispatch(new ClassroomAction.ToggleChatCollapse(false));
    }

    this.utilService.safeClipboardEvent.subscribe((clipboardEvent: ClipboardEvent): void => {
      this.inputValue = (clipboardEvent.target as HTMLInputElement).value;
      this.onInputChange();
    });
  }

  private isChatLimitExceeded(): boolean {
    const existingMessagesLength: number = this.classroom.messages.reduce((result: number, currentMessage: Message): number => {
      return result + (currentMessage.senderId === this.localUser.id ? currentMessage.text.length : 0);
    }, 0);

    return  CHAT_MESSAGES_LIMIT - existingMessagesLength - this.inputValue.length < 0;
  }

  public onInputChange(): void {
    if (this.localUser.accessType === AccessType.STUDENT) {
      if (this.isChatLimitExceeded()) {
        this.studentChatInput.nativeElement.value = this.lastInputValue;
        this.inputValue = this.lastInputValue;
        if (!this.showPopUp){
          setTimeout(() => {
            this.showPopUp = false;
          }, 2000);
        }
        this.showPopUp = true;
      } else {
        this.lastInputValue = this.inputValue;
      }
    }
  }

  public sendMessage(message: string): void {
    if (message.length <= 0 || (this.localUser.accessType === AccessType.STUDENT && this.isChatLimitExceeded())) {
      return;
    }

    const messageModel = new Message();
    messageModel.text = message;
    messageModel.senderId = this.localUser.id;

    if (this.localUser.accessType === AccessType.STUDENT) {
      messageModel.messageType = MessageType.TO_STAFF;
      messageModel.targetId = -1;
    } else {
      messageModel.messageType = MessageType.TO_ALL;
      messageModel.targetId = null;
    }

    this.studentChatInput.nativeElement.value = '';
    this.lastInputValue = '';
    this.inputValue = '';

    this.chatService.sendMessage(messageModel).subscribe(result => {
      if (messageModel.targetId === -1) { /* when student sends message to staff */
        this.chatService.onMessage(result.data as MessageRecord);
      }
      this.chatService.sendClassWideMessage(messageModel).subscribe(result1 => {
      });
    });
  }

  private listenStoreChanges(): void {
    this.store.select(ClassroomState).subscribe(classroom => {
      this.classroom = classroom;
    });

    this.store.select(LocalUserState.getLocalUser).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });

    this.store.select(RemoteUsersInfoState.getUsersInfo).subscribe(usersInfoById => {
      this.remoteUsers = usersInfoById;
    });
  }

  public toggleCollapseChat(): void {
    this.store.dispatch(new ClassroomAction.ToggleChatCollapse(!this.classroom.isChatCollapsed));
  }

  @HostListener('window:mousemove', ['$event'])
  private onMouseMove(event: MouseEvent): void {
    this.mouse = {x: event.clientX, y: event.clientY};

    if (this.isInResizeState) {
      this.resize();
    }
  }

  @HostListener('window:mouseup', ['$event'])
  private onMouseUp(): void {
    this.isInResizeState = false;
  }

  public startResize(): void {
    this.isInResizeState = true;
  }

  private resize(): void {
    this.height = window.innerHeight - this.mouse.y;
  }
}
